// capture.ts — the tools that let an agent SEE the motion (JUICE-RIG §5–7).
//
// Two capture paths, deliberately:
//   • MediaRecorder on the fx canvas -> a .webm (juice-off vs juice-on, or share clips).
//   • A frame-strip contact sheet: sample the fx canvas at fixed offsets after an event and
//     stitch them into one horizontal PNG — a keyframe timeline you can read as a sequence.
//
// The fx canvas only carries the particle layer; DOM-driven motion (tile pop, shake) is best
// captured externally via the Chrome MCP + seeded replay, which is exactly what §5 sets up. Both
// paths are exposed on `window.__juice` so the /feel skill can drive them headlessly.

import { fxCanvas } from "./fxlayer";
import { bus, type EventName } from "../core/bus";

let recorder: MediaRecorder | null = null;
let chunks: BlobPart[] = [];

function download(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Start recording the fx canvas to webm. */
export function startRecording(): void {
  const stream = fxCanvas().captureStream(60);
  chunks = [];
  recorder = new MediaRecorder(stream, { mimeType: "video/webm" });
  recorder.ondataavailable = (e) => e.data.size && chunks.push(e.data);
  recorder.start();
  console.log("[capture] recording…");
}

/** Stop recording and download the clip. */
export function stopRecording(): void {
  if (!recorder) return;
  recorder.onstop = () => download(new Blob(chunks, { type: "video/webm" }), "duly-noted.webm");
  recorder.stop();
  recorder = null;
}

/**
 * Fire a test event, then sample the fx canvas `frames` times over `spanMs` and stitch a
 * horizontal contact sheet PNG. Read it to critique the motion frame by frame.
 */
export async function frameStrip(
  event: EventName,
  payload: any,
  frames = 8,
  spanMs = 640,
): Promise<void> {
  const src = fxCanvas();
  const w = 160;
  const h = 90;
  const sheet = document.createElement("canvas");
  sheet.width = w * frames;
  sheet.height = h;
  const sctx = sheet.getContext("2d")!;

  bus.emit(event, payload);
  const step = spanMs / frames;
  for (let i = 0; i < frames; i++) {
    await new Promise((r) => setTimeout(r, step));
    sctx.fillStyle = "#111";
    sctx.fillRect(i * w, 0, w, h);
    sctx.drawImage(src, i * w, 0, w, h);
    sctx.strokeStyle = "#0f0";
    sctx.strokeRect(i * w, 0, w, h);
  }
  download(await canvasToBlob(sheet), "framestrip.png");
}

function canvasToBlob(c: HTMLCanvasElement): Promise<Blob> {
  return new Promise((res) => c.toBlob((b) => res(b!), "image/png"));
}
